// ===========================================
// Custom Email API
// ===========================================
// API client for custom email campaigns feature

import { apiClient } from '../services/api';
import {
  CustomEmail,
  CustomEmailListItem,
  CreateCustomEmailData,
  UpdateCustomEmailData,
  ScheduleCustomEmailData,
  TestEmailData,
  CustomEmailFilters,
  PaginatedCustomEmailResponse,
  RecipientListPreview,
  CustomEmailSendQueuedResult,
  TestEmailResult,
  CustomEmailDistributionStats,
  CustomEmailDetailedAnalytics,
  CustomEmailAnalyticsRecord,
  CustomEmailAttachment,
  AttachmentUploadResult,
} from '../types/customEmail.types';

const BASE_URL = '/admin/custom-emails';

// ===========================================
// API RESPONSE WRAPPER
// ===========================================

interface ApiResponse<T> {
  status: string;
  data: T;
  message?: string;
  meta?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// ===========================================
// CRUD OPERATIONS
// ===========================================

/**
 * Create a new custom email
 */
export async function createCustomEmail(
  data: CreateCustomEmailData
): Promise<CustomEmail> {
  const response = await apiClient.post<ApiResponse<CustomEmail>>(BASE_URL, data);
  return response.data;
}

/**
 * Get all custom emails with filters (admin view)
 */
export async function getCustomEmails(
  filters?: CustomEmailFilters
): Promise<PaginatedCustomEmailResponse> {
  const response = await apiClient.get<ApiResponse<CustomEmailListItem[]>>(
    BASE_URL,
    { params: filters }
  );
  return {
    data: response.data,
    total: response.meta?.total || response.data.length,
    page: response.meta?.page || 1,
    limit: response.meta?.limit || 20,
    totalPages: response.meta?.totalPages || 1,
  };
}

/**
 * Get a single custom email by ID
 */
export async function getCustomEmail(customEmailId: string): Promise<CustomEmail> {
  const response = await apiClient.get<ApiResponse<CustomEmail>>(
    `${BASE_URL}/${customEmailId}`
  );
  return response.data;
}

/**
 * Update a custom email
 */
export async function updateCustomEmail(
  customEmailId: string,
  data: UpdateCustomEmailData
): Promise<CustomEmail> {
  const response = await apiClient.patch<ApiResponse<CustomEmail>>(
    `${BASE_URL}/${customEmailId}`,
    data
  );
  return response.data;
}

/**
 * Delete a custom email
 */
export async function deleteCustomEmail(customEmailId: string): Promise<void> {
  await apiClient.delete(`${BASE_URL}/${customEmailId}`);
}

/**
 * Duplicate a custom email
 */
export async function duplicateCustomEmail(
  customEmailId: string
): Promise<CustomEmail> {
  const response = await apiClient.post<ApiResponse<CustomEmail>>(
    `${BASE_URL}/${customEmailId}/duplicate`
  );
  return response.data;
}

// ===========================================
// SCHEDULING
// ===========================================

/**
 * Schedule a custom email for future sending
 */
export async function scheduleCustomEmail(
  customEmailId: string,
  data: ScheduleCustomEmailData
): Promise<CustomEmail> {
  const response = await apiClient.post<ApiResponse<CustomEmail>>(
    `${BASE_URL}/${customEmailId}/schedule`,
    data
  );
  return response.data;
}

/**
 * Cancel a scheduled custom email
 */
export async function cancelScheduledEmail(
  customEmailId: string
): Promise<CustomEmail> {
  const response = await apiClient.post<ApiResponse<CustomEmail>>(
    `${BASE_URL}/${customEmailId}/cancel`
  );
  return response.data;
}

// ===========================================
// RECIPIENTS
// ===========================================

/**
 * Get recipient list preview
 */
export async function getRecipientPreview(
  customEmailId: string
): Promise<RecipientListPreview> {
  const response = await apiClient.get<ApiResponse<RecipientListPreview>>(
    `${BASE_URL}/${customEmailId}/recipients`
  );
  return response.data;
}

// ===========================================
// SENDING
// ===========================================

/**
 * Send a test email
 */
export async function sendTestEmail(
  customEmailId: string,
  data: TestEmailData
): Promise<TestEmailResult> {
  const response = await apiClient.post<ApiResponse<TestEmailResult>>(
    `${BASE_URL}/${customEmailId}/test`,
    data
  );
  return response.data;
}

/**
 * Send custom email to all recipients
 */
/**
 * QUEUE a custom email for sending. Returns a 202 in milliseconds having sent
 * nothing — progress comes from polling the campaign's status and stats.
 *
 * ⚠️ Previously typed `CustomEmailDistributionResult`, whose `success`,
 * `sentCount` and `failedCount` were `undefined` at runtime: the inline send
 * that produced them was replaced by a background job and the type was never
 * updated (/qa Phase 5, HIGH-1).
 */
export async function sendCustomEmail(
  customEmailId: string
): Promise<CustomEmailSendQueuedResult> {
  const response = await apiClient.post<ApiResponse<CustomEmailSendQueuedResult>>(
    `${BASE_URL}/${customEmailId}/send`
  );
  return response.data;
}

// ===========================================
// ANALYTICS
// ===========================================

/**
 * Get distribution stats for a custom email
 */
export async function getCustomEmailStats(
  customEmailId: string
): Promise<CustomEmailDistributionStats> {
  const response = await apiClient.get<ApiResponse<CustomEmailDistributionStats>>(
    `${BASE_URL}/${customEmailId}/analytics`
  );
  return response.data;
}

/**
 * Get detailed analytics including top clicked URLs
 */
export async function getDetailedAnalytics(
  customEmailId: string
): Promise<CustomEmailDetailedAnalytics> {
  const response = await apiClient.get<ApiResponse<CustomEmailDetailedAnalytics>>(
    `${BASE_URL}/${customEmailId}/analytics/detailed`
  );
  return response.data;
}

/**
 * Get analytics records (delivery log)
 */
export async function getAnalyticsRecords(
  customEmailId: string,
  params?: { page?: number; limit?: number }
): Promise<{ records: CustomEmailAnalyticsRecord[]; total: number }> {
  const response = await apiClient.get<
    ApiResponse<{ records: CustomEmailAnalyticsRecord[]; total: number }>
  >(`${BASE_URL}/${customEmailId}/analytics/records`, { params });
  return response.data;
}

// ===========================================
// ATTACHMENTS
// ===========================================

/**
 * Get all attachments for a custom email
 */
export async function getAttachments(
  customEmailId: string
): Promise<CustomEmailAttachment[]> {
  const response = await apiClient.get<ApiResponse<CustomEmailAttachment[]>>(
    `${BASE_URL}/${customEmailId}/attachments`
  );
  return response.data;
}

/**
 * Upload an attachment for a custom email
 */
export async function uploadAttachment(
  customEmailId: string,
  file: File
): Promise<AttachmentUploadResult> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post<ApiResponse<AttachmentUploadResult>>(
    `${BASE_URL}/${customEmailId}/attachments`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
}

/**
 * Delete an attachment
 */
export async function deleteAttachment(
  customEmailId: string,
  attachmentId: string
): Promise<void> {
  await apiClient.delete(`${BASE_URL}/${customEmailId}/attachments/${attachmentId}`);
}

// ===========================================
// IMAGES
// ===========================================

export interface EmailImageUploadResult {
  url: string;
  filename: string;
  width?: number;
  height?: number;
}

/**
 * Upload an image for use in email content
 * Returns a URL that can be embedded in the email HTML
 */
export async function uploadEmailImage(
  file: File
): Promise<EmailImageUploadResult> {
  const formData = new FormData();
  formData.append('image', file);

  const response = await apiClient.post<ApiResponse<EmailImageUploadResult>>(
    `${BASE_URL}/images`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
}
